import { captureError } from '@smg-real-estate/browser-rum';
import { computedAsync } from '@vueuse/core';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { getUser } from '@/api/users';
import { GUEST_EMAIL } from '@/constants/guest';
import { parseJwt } from '@/helpers/authUtils';
import { captureErrorOnce } from '@/helpers/errorUtils';
import { isIs24LegacyGuestWhitelisted } from '@/helpers/is24LegacyGuestWhitelisted';
import { LoginStatus } from '@/services/tracking/GtmTrackingService';
import { useMyAdsStore } from '@/stores/myAds';
import { UserGroup, UserType, type AuthorizedUser } from '@/types';

/**
 * Store for managing the user info of the insertion funnel including:
 * - Authenticated user from Auth0
 * - User type from NIF API
 */
export const useUserStore = defineStore('user', () => {
  const authenticatedUser = ref<AuthorizedUser>();
  const userType = ref<UserType>(UserType.TENANT);
  // Used only to determine if the nif-api user data is loaded
  const isUserLoaded = ref(false);
  const isLegacyIs24GuestAllowlisted = computedAsync(
    async () => await isIs24LegacyGuestWhitelisted(authenticatedUser.value?.userId),
    false,
  );

  // userId is expected to be in place, default to empty string if not set
  const userId = computed<string>(() => authenticatedUser.value?.userId ?? '');
  const isGuest = computed<boolean>(() => authenticatedUser.value?.isGuest ?? false);
  // TODO: collapse to isGuest once all legacy IS24 guests are migrated
  const isEffectiveGuest = computed<boolean>(
    () => isGuest.value && !isLegacyIs24GuestAllowlisted.value,
  );
  const hasUser = computed<boolean>(() => !!authenticatedUser.value);
  const userEmail = computed<string>(() => authenticatedUser.value?.email ?? '');
  const userGroups = computed<UserGroup[]>(() => authenticatedUser.value?.groups ?? []);
  const isReturnUser = computed<boolean>(() =>
    useMyAdsStore().insertions.some((insertion) => !!insertion.firstPublishedDate),
  );
  const loginStatus = computed<LoginStatus>(() => {
    if (!authenticatedUser.value) {
      return LoginStatus.NOT_LOGGED_IN;
    }
    if (authenticatedUser.value.isGuest) {
      return LoginStatus.NOT_LOGGED_IN;
    }
    return LoginStatus.LOGGED_IN;
  });

  /**
   * Fetches the user type from the NIF API.
   *
   * @remarks
   * Auth0 does not include userType, which is managed within NIF only.
   * For tracking purpose, we need to fetch it from NIF API.
   *
   * @param userId - The ID of the user to fetch the user type for.
   */
  async function fetchUserType(userId: string) {
    if (!userId) return;
    try {
      const response = await getUser(userId);
      userType.value = response?.userType ?? UserType.TENANT;
      isUserLoaded.value = true;
    } catch (error) {
      captureErrorOnce(error, { service: 'insertion-api', action: 'get-user', userId });
    }
  }

  function setAuthenticatedUser(newUser: AuthorizedUser, force = false) {
    if (!force && authenticatedUser.value?.userId) return;
    isUserLoaded.value = false;
    authenticatedUser.value = newUser;
    isLegacyIs24GuestAllowlisted.value = false;
    void fetchUserType(newUser.userId);
  }

  async function setGuestUser(guestToken: string) {
    isUserLoaded.value = false;
    try {
      const parsedToken = parseJwt(guestToken);
      const newUser: AuthorizedUser = {
        userId: parsedToken?.payload.ownerId,
        email: GUEST_EMAIL,
        logonId: parsedToken?.payload.ownerId,
        groups: parsedToken?.payload.groups,
        emailVerified: true,
        isGuest: true,
      };
      authenticatedUser.value = newUser;
      void fetchUserType(newUser.userId);
    } catch (error) {
      captureError(error, { action: 'parse-jwt' });
    }
  }

  function $reset() {
    authenticatedUser.value = undefined;
    userType.value = UserType.TENANT;
    isUserLoaded.value = false;
  }

  return {
    authenticatedUser,
    setAuthenticatedUser,
    setGuestUser,
    userId,
    isGuest,
    isEffectiveGuest,
    hasUser,
    userType,
    userEmail,
    userGroups,
    loginStatus,
    isReturnUser,
    isUserLoaded,
    isLegacyIs24GuestAllowlisted,
    fetchUserType,
    $reset,
  };
});
